<template>
  <div class="markdown-editor">
    <textarea />
    <div v-if="addingImage" class="reading-links-modal">
      <div class="row">
        <div class="small-12 columns">
          <div class="reading-image-upload-container">
            <div class="row">
              <div class="small-12 columns">
                <UploadFile
                  @codingFileUploadSuccess="handleUploadSuccessEvent"
                />
              </div>
              <div class="row">
                <div class="small-6 columns">Search</div>
                <div class="small-6 columns">
                  <input
                    v-model="searchString"
                    type="text"
                    @input="filterLinks"
                  />
                </div>
              </div>
              <div class="row">
                <div class="small-12 columns" @click="orderByDate">
                  Order By Date
                </div>
              </div>
            </div>
            <div class="reading-links-container" style="padding:20px;">
              <div class="row">
                <div
                  v-for="link in links"
                  :key="link.id"
                  class="small-1 columns "
                  :style="
                    currentSelectedLink.id === link.id
                      ? 'background-color:#afe075'
                      : ''
                  "
                >
                  <div class="link-item-container">
                    <div class="row">
                      <div class="small-12 columns">
                        <img
                          :src="link.public_url"
                          alt=""
                          style="width:100%;max-width:60px;max-height:60px;margin:auto;"
                        />
                      </div>
                      <div
                        class="small-12 columns"
                        style="font-size:0.4em;word-break:break-all;"
                      >
                        {{ link.name }}
                      </div>
                      <div class="small-12 columns">
                        <div>
                          <button
                            style="display:block;width:40px;margin:auto;padding:0;font-size:0.6em;"
                            @click="selectLink(link)"
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row" style="margin:10px 0px;text-align:center;">
              <div class="small-4 columns">
                <button @click="getLinks">Refresh Links</button>
              </div>
              <div class="small-4 columns">
                <button
                  class="button-green"
                  @click="updateReadingContentWithLink"
                >
                  Update
                </button>
              </div>
              <div class="small-4 columns">
                <button class="button-red" @click="addingImage = false">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SimpleMDE from "simplemde"
// eslint-disable-next-line import/no-extraneous-dependencies
import marked from "marked"
import siteConfig from "@/utils/siteConfig"
import UploadFile from "@/components/shared/fileUpload/SingleFileUpload"

export default {
  name: "MarkdownEditor",
  components: { UploadFile },
  props: {
    value: String,
    previewClass: String,
    autoinit: {
      type: Boolean,
      default() {
        return true
      },
    },
    highlight: {
      type: Boolean,
      default() {
        return false
      },
    },
    sanitize: {
      type: Boolean,
      default() {
        return false
      },
    },
    configs: {
      type: Object,
      default() {
        return {
          autofocus: true,
          forceSync: true,
          indentWithTabs: true,
          toolbar: [
            {
              name: "bold",
              action: SimpleMDE.toggleBold,
              className: "fa fa-bold",
              title: "Bold",
            },
            {
              name: "italic",
              action: SimpleMDE.toggleItalic,
              className: "fa fa-italic",
              title: "Italic",
            },
            {
              name: "strikethrough",
              action: SimpleMDE.toggleStrikethrough,
              className: "fa fa-strikethrough",
              title: "strikethrough",
            },
            "|",
            {
              name: "heading",
              action: SimpleMDE.toggleHeadingSmaller,
              className: "fa fa-header fa-header-x fa-header-1",
              title: "heading",
            },

            {
              name: "heading-bigger",
              action: SimpleMDE.toggleHeadingBigger,
              className: "fa fa-lg fa-header",
              title: "heading-bigger",
            },
            {
              name: "heading-smaller	",
              action: SimpleMDE.toggleHeadingSmaller,
              className: "fa fa-header",
              title: "heading-smaller	",
            },
            "|",
            {
              name: "code",
              action: SimpleMDE.toggleCodeBlock,
              className: "fa fa-code",
              title: "code",
            },
            {
              name: "quote",
              action: SimpleMDE.toggleBlockquote,
              className: "fa fa-quote-left",
              title: "quote",
            },
            "|",
            {
              name: "unordered-list",
              action: SimpleMDE.toggleUnorderedList,
              className: "fa fa-list-ul",
              title: "unordered-list",
            },
            {
              name: "ordered-list",
              action: SimpleMDE.toggleOrderedList,
              className: "fa fa-list-ol",
              title: "ordered-list",
            },
            "|",
            {
              name: "link",
              action: SimpleMDE.drawLink,
              className: "fa fa-link",
              title: "link",
            },
            {
              name: "custom",
              action: editor => {
                this.addingImage = true

                // / GET CURSOR PRESENT POSITION
                const cm = this.simplemde.codemirror
                const endPoint = cm.getCursor("end")

                this.content = editor.element.value
                this.positionObject = endPoint
              },
              className: "fa fa-picture-o",
              title: "Upload Image",
            },

            "|", // Separator,
            {
              name: "horizontal-rule",
              action: SimpleMDE.drawHorizontalRule,
              className: "fa fa-minus",
              title: "horizontal-rule",
            },
          ],
        }
      },
    },
  },
  data() {
    return {
      addingImage: false,
      content: "",
      positionObject: {},
      links: [],
      injectedIDs: [],
      currentSelectedLink: {},
      attachments: [],
      searchString: "",
      backUpLinks: [],
    }
  },
  watch: {
    value(val) {
      if (val === this.simplemde.value()) return
      this.simplemde.value(val)
    },
  },
  mounted() {
    if (this.autoinit) this.initialize()
  },
  activated() {
    const editor = this.simplemde
    if (!editor) return
    const isActive = editor.isSideBySideActive() || editor.isPreviewActive()
    if (isActive) editor.toggleFullScreen()
  },
  destroyed() {
    this.simplemde = null
  },
  methods: {
    filterLinks() {
      this.links = this.backUpLinks.filter(item => {
        if (item.original_name.includes(this.searchString)) {
          return item
        }
        throw new Error("can't find item")
      })
      // this.links = this.filterLinks.
    },
    orderByDate() {
      this.links = this.backUpLinks.sort((a, b) => {
        a = a.created_on
          .split("/")
          .reverse()
          .join("")
        b = b.created_on
          .split("/")
          .reverse()
          .join("")
        // eslint-disable-next-line no-nested-ternary
        return a < b ? 1 : a > b ? -1 : 0
      })
    },
    handleUploadSuccessEvent() {
      this.getLinks()
    },
    selectLink(link) {
      this.currentSelectedLink = link
    },
    getLinks() {
      this.axios
        .get(`${siteConfig.siteURL}uploads/list`, {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": localStorage.token,
          },
        })
        .then(res => {
          this.links = res.data.data
          this.backUpLinks = res.data.data
        })
    },
    getIdsFromEncodedString(string) {
      let ids = string.match(/(\[\[.*?\]\])/g)
      if (ids === null || ids === undefined) {
        return []
      }
      ids = ids.map(item => {
        const { length } = item
        return item.slice(2, length - 2)
      })

      const temp = {}
      for (let i = 0; i < ids.length; i++) temp[ids[i]] = true
      const uniqueIDs = []
      Object.keys(temp).forEach(k => uniqueIDs.push(k))
      return uniqueIDs || []
    },
    getAttachmentObjectsFromIDs(ids) {
      if (ids === undefined || ids.length === 0) {
        this.attachments = []
        return []
      }
      const finalObjects = []
      for (let i = 0; i < ids.length; i++) {
        for (let j = 0; j < this.links.length; j++) {
          if (this.links[j].id === ids[i]) {
            finalObjects.push(this.links[j])
          }
        }
      }
      return finalObjects
    },
    getAttachments() {
      return this.attachments
    },
    updateReadingContentWithLink() {
      this.getLinks()
      const encodedString = this.replaceStringAt()
      // / IDs
      const injectedIDs = this.getIdsFromEncodedString(encodedString)
      this.injectedIDs = injectedIDs
      // / attachments
      this.attachments = this.getAttachmentObjectsFromIDs(injectedIDs)

      this.$emit("imageAdded", {
        encodedString,
        attachments: this.attachments,
      })
      this.addingImage = false
    },
    replaceStringAt() {
      const totalString = this.content
      const { positionObject } = this
      // ///
      const id = positionObject.ch
      const { line } = positionObject
      const string = totalString.split("\n")[line]
      const obj = this.currentSelectedLink
      const encodedLinkString = `[[${obj.id}]]`
      const leftPartString = string.substr(0, id)
      const rightPartString = string.substr(id)
      const encodedString = leftPartString + encodedLinkString + rightPartString
      const finalString = []
      for (let i = 0; i < totalString.split("\n").length; i++) {
        if (i !== line) {
          finalString.push(totalString.split("\n")[i])
        } else {
          finalString.push(encodedString)
        }
      }
      return finalString.join("\n")
    },
    initialize() {
      const configs = Object.assign(
        {
          element: this.$el.firstElementChild,
          initialValue: this.value,
          renderingConfig: {},
        },
        this.configs
      )

      if (configs.initialValue) {
        this.$emit("input", configs.initialValue)
      }
      if (this.highlight) {
        configs.renderingConfig.codeSyntaxHighlighting = true
      }
      marked.setOptions({ sanitize: this.sanitize })
      this.simplemde = new SimpleMDE(configs)
      const className = this.previewClass || ""
      this.addPreviewClass(className)
      this.bindingEvents()
    },
    bindingEvents() {
      this.simplemde.codemirror.on("change", () => {
        this.$emit("input", this.simplemde.value())
      })
    },
    addPreviewClass(className) {
      const wrapper = this.simplemde.codemirror.getWrapperElement()
      const preview = document.createElement("div")
      wrapper.nextSibling.className += ` ${className}`
      preview.className = `editor-preview ${className}`
      wrapper.appendChild(preview)
    },
  },
}
</script>

<style>
.controls-file-uploader {
}
.reading-links-container {
  overflow-y: scroll;
}
.reading-links-modal {
  overflow: scroll;
  position: fixed;
  z-index: 100;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
}
.reading-image-upload-container {
  z-index: 200;
  top: 100px;
  width: 90%;
  height: auto;
  margin: auto;
  margin-top: 100px;
  border: 1px solid black;
  background-color: white;
}
.link-item-container {
  padding: 10px;
}
.markdown-editor .markdown-body {
  padding: 0.5em;
}

.markdown-editor .editor-preview-active,
.markdown-editor .editor-preview-active-side {
  display: block;
}
</style>
